import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

/** Figma 8441:185752 — shared confirm dialog chrome (delete / status toggle). */
export const confirmDeleteDialogContentClass = cn(
  'max-w-[600px] gap-0 border-[#e7e9ef] bg-white p-8 shadow-[0px_1px_4px_-1px_rgba(38,45,66,0.05),0px_8px_16px_-4px_rgba(38,45,66,0.1)] sm:rounded-2xl'
)

export const confirmDeleteCancelBtnClass =
  'h-11 rounded-xl border-[#e7e9ef] bg-white px-3 py-3 text-sm font-medium leading-none text-[#171a20] hover:bg-[#f4f5f8]'

export const confirmDeleteDangerBtnClass =
  'h-11 rounded-xl border-0 bg-[#d63b33] px-3 py-3 text-sm font-medium leading-none text-white hover:bg-[#c2342d]'

export const confirmBrandBtnClass =
  'h-11 rounded-xl border border-[#ff5a1f] bg-[#ff5a1f] px-3 py-3 text-sm font-medium leading-none text-white hover:bg-[#e6511c]'

export interface ConfirmDeleteDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: ReactNode
  confirmLabel: string
  onConfirm: () => void | Promise<void>
  busy?: boolean
  cancelLabel?: string
  /** Primary CTA style. Defaults to the orange brand CTA. */
  confirmVariant?: 'danger' | 'brand'
  /**
   * When set, replaces the primary confirm button (e.g. technician blocked by
   * future jobs → offer Deactivate instead).
   */
  alternateAction?: {
    label: string
    onClick: () => void
  }
}

// Figma 8441:185752 — Cancel + confirm shell used by portal confirm dialogs.
export function ConfirmDeleteDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  onConfirm,
  busy = false,
  cancelLabel,
  confirmVariant = 'brand',
  alternateAction,
}: ConfirmDeleteDialogProps) {
  const { t } = useTranslation('common')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={confirmDeleteDialogContentClass}
        overlayClassName='bg-black/70 backdrop-blur-[2px]'
        showCloseButton={false}
      >
        <div className='flex flex-col gap-6'>
          <div className='flex flex-col gap-2'>
            <DialogTitle className='font-heading text-base font-semibold leading-[1.2] text-[#171a20]'>
              {title}
            </DialogTitle>
            <p className='text-sm font-normal leading-[1.4] text-[#171a20]'>
              {description}
            </p>
          </div>

          <div className='flex items-center justify-end gap-4'>
            <Button
              variant='outline'
              className={confirmDeleteCancelBtnClass}
              onClick={() => onOpenChange(false)}
              disabled={busy}
            >
              {cancelLabel ?? t('actions.cancel')}
            </Button>
            {alternateAction ? (
              <Button
                variant='outline'
                className={confirmDeleteCancelBtnClass}
                onClick={alternateAction.onClick}
                disabled={busy}
              >
                {alternateAction.label}
              </Button>
            ) : (
              <Button
                className={
                  confirmVariant === 'brand'
                    ? confirmBrandBtnClass
                    : confirmDeleteDangerBtnClass
                }
                disabled={busy}
                onClick={onConfirm}
              >
                {confirmLabel}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default ConfirmDeleteDialog
